import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModules } from '../shared/ModulesContext';
import AssistantChat from '../screens/assistant/AssistantChat';
import useVoiceRecording from '../screens/assistant/useVoiceRecording';
import './AssistantChatWidget.css';

const STORAGE_KEY = 'assistant_widget_open';
const POSITION_STORAGE_KEY = 'assistant_widget_position';
const DRAG_THRESHOLD_PX = 6;

const AssistantChatWidget = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { hasModule, loading } = useModules();
  const [open, setOpen] = useState(false);
  const [externalTranscript, setExternalTranscript] = useState(null);
  const [fabPosition, setFabPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAssistantWorking, setIsAssistantWorking] = useState(false);
  const { isRecording, isTranscribing, startRecording, stopRecording } = useVoiceRecording({ language: i18n.language });

  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const dragMovedRef = useRef(false);
  const actionsRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const triggerVoiceCapture = () => {
    if (isTranscribing) return;
    startRecording((text) => {
      if (!text) return;
      setExternalTranscript({ id: Date.now(), text: String(text).trim() });
    });
  };

  const clearLongPressTimer = () => {
    if (!longPressTimerRef.current) return;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  };

  const handleMainPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    const actionsEl = actionsRef.current;
    const rect = actionsEl?.getBoundingClientRect();

    const originX = fabPosition?.x ?? (rect ? rect.left : window.innerWidth - 80);
    const originY = fabPosition?.y ?? (rect ? rect.top : window.innerHeight - 140);

    dragStateRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX,
      originY,
    };

    dragMovedRef.current = false;
    longPressTriggeredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      triggerVoiceCapture();
    }, 360);
  };

  const handleMainPointerUp = (event) => {
    clearLongPressTimer();
    if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  useEffect(() => {
    return () => clearLongPressTimer();
  }, []);

  useEffect(() => {
    try {
      setOpen(localStorage.getItem(STORAGE_KEY) === '1');
    } catch {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, open ? '1' : '0');
    } catch {
      // Ignore storage errors in private mode.
    }
  }, [open]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(POSITION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
        setFabPosition({ x: parsed.x, y: parsed.y });
      }
    } catch {
      // Ignore invalid storage payload.
    }
  }, []);

  useEffect(() => {
    if (!fabPosition) return;
    try {
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(fabPosition));
    } catch {
      // Ignore storage errors.
    }
  }, [fabPosition]);

  useEffect(() => {
    const clampToViewport = (x, y) => {
      const actionsEl = actionsRef.current;
      if (!actionsEl) return { x, y };

      const width = actionsEl.offsetWidth || 56;
      const height = actionsEl.offsetHeight || 56;
      const minX = 8;
      const minY = 8;
      const maxX = Math.max(minX, window.innerWidth - width - 8);
      const maxY = Math.max(minY, window.innerHeight - height - 8);

      return {
        x: Math.min(Math.max(x, minX), maxX),
        y: Math.min(Math.max(y, minY), maxY),
      };
    };

    const onPointerMove = (event) => {
      const drag = dragStateRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      if (!dragMovedRef.current && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
        dragMovedRef.current = true;
        setIsDragging(true);
        clearLongPressTimer();
      }

      if (!dragMovedRef.current) return;

      const next = clampToViewport(drag.originX + dx, drag.originY + dy);
      setFabPosition(next);
    };

    const finishDrag = (event) => {
      const drag = dragStateRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;
      dragStateRef.current.active = false;
      dragStateRef.current.pointerId = null;
      clearLongPressTimer();
      setIsDragging(false);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
    };
  }, []);

  const isChatRoute = useMemo(() => /^\/chat(?:\/|$)/.test(location.pathname), [location.pathname]);

  if (loading || !hasModule('ai-assistant') || isChatRoute) {
    return null;
  }

  return (
    <>
      {open && <div className="assistant-widget-overlay" onClick={() => setOpen(false)} />}

      <div
        ref={actionsRef}
        className={`assistant-widget-actions${open || isRecording || isTranscribing || isAssistantWorking ? ' is-expanded' : ''}${isDragging ? ' is-dragging' : ''}${isAssistantWorking ? ' is-working' : ''}`}
        style={fabPosition ? { left: `${fabPosition.x}px`, top: `${fabPosition.y}px`, right: 'auto', bottom: 'auto' } : undefined}
      >
        {(isRecording || isTranscribing) && (
          <span className="assistant-widget-status" role="status" aria-label={t('assistant.working', 'AI asistent pracuje')}>
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          </span>
        )}
        <button
          type="button"
          className="assistant-widget-fab assistant-widget-fab--voice"
          onPointerDown={triggerVoiceCapture}
          onPointerUp={stopRecording}
          onPointerCancel={stopRecording}
          onPointerLeave={() => { if (isRecording) stopRecording(); }}
          onTouchStart={(e) => e.preventDefault()}
          aria-label={t('assistant.voiceInput', 'Voice input')}
          title={isRecording ? t('assistant.recording', 'Recording…') : isTranscribing ? t('assistant.transcribing', 'Transcribing…') : t('assistant.voiceInput', 'Voice input')}
        >
          {isTranscribing
            ? <span className="spinner-border spinner-border-sm" />
            : <i className={`ti ${isRecording ? 'ti-microphone-off' : 'ti-microphone'}`} aria-hidden="true" />}
        </button>

        <button
          type="button"
          className="assistant-widget-fab"
          onPointerDown={handleMainPointerDown}
          onPointerUp={handleMainPointerUp}
          onPointerLeave={clearLongPressTimer}
          onPointerCancel={handleMainPointerUp}
          onClick={() => {
            if (dragMovedRef.current) {
              dragMovedRef.current = false;
              return;
            }
            if (longPressTriggeredRef.current) {
              longPressTriggeredRef.current = false;
              return;
            }
            setOpen((prev) => !prev);
          }}
          aria-label={t('chat.aiAssistant')}
          title={t('assistant.holdToTalkHint', 'AI Assistant (hold for voice)')}
        >
          {isAssistantWorking && <span className="assistant-widget-working-ring" aria-hidden="true" />}
          <i className="ti ti-message-chatbot" aria-hidden="true" />
        </button>
      </div>

      <aside className={`assistant-widget-panel${open ? ' is-open' : ''}`} aria-hidden={!open}>
        <div className="assistant-widget-header">
          <div className="assistant-widget-title-wrap">
            <i className="ti ti-stars" aria-hidden="true" />
            <span className="assistant-widget-title">{t('chat.aiAssistant')}</span>
          </div>
          <button
            type="button"
            className="assistant-widget-close"
            onClick={() => setOpen(false)}
            aria-label={t('common.close')}
          >
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </div>
        <div className="assistant-widget-body">
          <AssistantChat embedded externalTranscript={externalTranscript} onWorkingChange={setIsAssistantWorking} />
        </div>
      </aside>
    </>
  );
};

export default AssistantChatWidget;
